/**
 * 规则 4：老手机号释放 —— 短信验证流程 Hook（本地管理，不入 store）
 * - token 非空 = 已通过短信校验，允许进入密码步
 * - 包含 60s 重发倒计时、发送验证码、校验验证码换取 releaseToken
 * - 发送锁与冷却倒计时由公共 useSmsCodeFlow 管理
 */
import { useState } from 'react';
import { sendPhoneReleaseCode, verifyPhoneRelease } from '../../../../../api/register';
import type { RegisterFormData } from '../../../../../stores/registerStore';
import type { ReleaseFlowState, ToastType } from '../types';
import { useSmsCodeFlow } from '../../../../../hooks/useSmsCodeFlow';

/** 老号释放流程初始状态 */
const INITIAL_RELEASE_FLOW: ReleaseFlowState = {
  code: '',
  token: null,
  verifying: false,
};

export function useReleaseFlow(
  formData: RegisterFormData,
  showToast: (message: string, type?: ToastType) => void,
) {
  const [releaseFlow, setReleaseFlow] = useState<ReleaseFlowState>(INITIAL_RELEASE_FLOW);
  const { countdown: releaseCountdown, sending: releaseSending, send } = useSmsCodeFlow();

  // 发送老号释放短信验证码
  const handleSendReleaseCode = async () => {
    try {
      await send(() =>
        sendPhoneReleaseCode({
          phone: formData.phone,
          fullName: formData.fullName,
          idCard: formData.idCard,
        }),
      );
      showToast('验证码已发送，5 分钟内有效', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : '发送失败，请稍后重试', 'error');
    }
  };

  // 校验短信验证码，拿到 releaseToken
  const handleVerifyReleaseCode = async () => {
    if (releaseFlow.verifying) return;
    const code = (releaseFlow.code || '').trim();
    if (!/^\d{6}$/.test(code)) {
      showToast('请输入 6 位数字验证码', 'info');
      return;
    }
    setReleaseFlow((prev) => ({ ...prev, verifying: true }));
    try {
      const resp = await verifyPhoneRelease({
        phone: formData.phone,
        code,
        fullName: formData.fullName,
        idCard: formData.idCard,
      });
      if (resp?.releaseToken) {
        setReleaseFlow((prev) => ({
          ...prev,
          verifying: false,
          token: resp.releaseToken,
        }));
        showToast('手机号验证成功，可继续注册', 'success');
      } else {
        setReleaseFlow((prev) => ({ ...prev, verifying: false }));
        showToast('验证失败，请重试', 'error');
      }
    } catch (error) {
      setReleaseFlow((prev) => ({ ...prev, verifying: false }));
      showToast(error instanceof Error ? error.message : '验证失败，请稍后重试', 'error');
    }
  };

  // 关闭弹窗时重置释放流程
  const resetReleaseFlow = () => {
    setReleaseFlow({ ...INITIAL_RELEASE_FLOW });
  };

  return {
    releaseFlow,
    setReleaseFlow,
    resetReleaseFlow,
    releaseCountdown,
    releaseSending,
    handleSendReleaseCode,
    handleVerifyReleaseCode,
  };
}
